import type { IntakeEvidenceInput } from "@convex/schemas/validators/intakeEvidence.lib";
import type { Id } from "@convex/_generated/dataModel";
import type { UtmTracking } from "@/hooks/use-utm-tracking";
import type { SpecsRecord } from "@/components/specs/types";
import type {
  PaymentPreference,
  DeliveryFrequency,
} from "@convex/constants/types.lib";

export interface RfqContactInput {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
}

export interface RfqLocationAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface RfqDeliverySchedule {
  frequency?: DeliveryFrequency;
  customFrequency?: string;
  neededByDate?: number;
  startDate?: string;
  endDate?: string;
  preferredDays?: string[];
}

export interface RfqLocationInput {
  addressId?: Id<"addresses">;
  address: RfqLocationAddress;
  quantity?: number;
  deliverySchedule?: RfqDeliverySchedule;
}

export interface RfqDimensionsInput {
  length?: number;
  width?: number;
  height?: number;
  capacity?: number;
  unit?: "in" | "mm" | "cm" | "ft";
  capacityUnit?: "gal" | "L" | "lbs";
  raw?: string;
}

export interface RfqPhotoInput {
  storageId: Id<"_storage">;
  description?: string;
}

export interface RfqAttachmentInput {
  fileId?: Id<"files">;
  storageId: Id<"_storage">;
  category: string;
  filename?: string;
  name?: string;
  description?: string;
  mimeType?: string;
  size?: number;
}

export interface RfqCreateArgs {
  rfqType: string;
  productTypeSlug: string;
  productTypeName?: string;
  quantity?: number;
  condition?: string;
  notes?: string;
  dimensions?: RfqDimensionsInput;
  productSpecs?: SpecsRecord;
  estimatedWeightLbs?: number;
  hasHazmat?: boolean;
  budgetCents?: number;
  askingPriceCents?: number;
  paymentPreference?: PaymentPreference;
  location?: RfqLocationInput;
  additionalLocations?: RfqLocationInput[];
  contact?: RfqContactInput;
  photos?: RfqPhotoInput[];
  attachments?: RfqAttachmentInput[];
  sourcePath?: string;
  sourceReferrer?: string;
  utmTracking?: UtmTracking;
  submissionChannel?: string;
  intakeEvidence?: IntakeEvidenceInput;
}

export interface RfqSubmitResult {
  rfqId: Id<"leads">;
  rfqNumber: string;
}

export interface RfqSubmitError {
  error: string;
}

export type RfqCreateResult = RfqSubmitResult | RfqSubmitError;

export function isRfqSubmitSuccess(
  result: RfqCreateResult,
): result is RfqSubmitResult {
  return "rfqId" in result;
}

export type RfqFormState = RfqCreateArgs;

export function createEmptyContact(): RfqContactInput {
  return {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  };
}

export function createInitialFormState(
  rfqType: RfqFormState["rfqType"] = "buy",
): Partial<RfqFormState> {
  return {
    rfqType,
    productTypeSlug: "",
    productTypeName: "",
    quantity: 0,
    location: createEmptyLocation(),
    contact: createEmptyContact(),
  };
}

export function createEmptyLocation(): RfqLocationInput {
  return {
    address: {
      line1: "",
      city: "",
      state: "",
      postalCode: "",
      country: "US",
    },
    deliverySchedule: {
      frequency: "one-time",
    },
  };
}

export interface ProductTypeOption {
  slug: string;
  displayName: string;
  pluralDisplayName?: string;
}
